import { create } from 'zustand'
import * as api from '../api'
import { useToastStore } from './toast'
import { useUIStore } from './ui'
import { useHistoryStore } from './history'
import type {
  CategoriesData, CellsMap, CellMetaMap,
  Debt, Reschedule, RecurringItem,
  BanksData, Department, SettingsData, Vendor, Item,
} from '../types'

// 履歴ID生成
function newHistId(): string {
  return `h_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
}

interface CashflowState {
  categories: CategoriesData
  cells: CellsMap
  cellMeta: CellMetaMap
  debts: Debt[]
  reschedules: Reschedule[]
  recurring: RecurringItem[]
  banks: BanksData
  departments: Department[]
  items: Item[]
  settings: SettingsData
  loading: boolean

  loadAll: () => Promise<void>
  saveCell: (vendorId: string, date: string, amount: number) => Promise<void>
  saveCellMeta: (vendorId: string, date: string, meta: Partial<import('../types').CellMeta>) => Promise<void>
  addVendor: (data: Partial<Vendor>) => Promise<void>
  updateVendor: (data: Partial<Vendor>) => Promise<void>
  removeVendor: (id: string) => Promise<void>
  addCategory: (data: { name: string; type: string }) => Promise<void>
  updateCategory: (data: { id: string; name?: string; sortOrder?: number }) => Promise<void>
  removeCategory: (id: string) => Promise<void>
  setOpeningBalance: (date: string, amount: number) => Promise<void>
  setBankBalance: (accountId: string, date: string, balance: number) => Promise<void>
  loadReschedules: () => Promise<void>
  loadDebts: () => Promise<void>
  loadRecurring: () => Promise<void>
  loadBanks: (from?: string, to?: string) => Promise<void>
  loadSettings: () => Promise<void>
}

export const useCashflowStore = create<CashflowState>((set, get) => ({
  categories: { income: [], expense: [] },
  cells: {},
  cellMeta: {},
  debts: [],
  reschedules: [],
  recurring: [],
  banks: { accounts: [], balances: {} },
  departments: [],
  items: [],
  settings: { settings: {}, fiscalYears: [], openingBalances: {} },
  loading: true,

  loadAll: async () => {
    // Promise.allSettled で部分失敗を許容する
    // 理由: allowed_pages 制限ユーザー（例: ['input','masters','recurring']）は
    //       /api/debts, /api/reschedules, /api/settings 等で403が返る。
    //       Promise.all だと1つの403で全結果が破棄されてカテゴリ等も消える。
    set({ loading: true })
    const results = await Promise.allSettled([
      api.categories.list(),
      api.cells.list(),
      api.debts.list(),
      api.reschedules.list(),
      api.recurring.list(),
      api.banks.list(),
      api.departments.list(),
      api.settings.list(),
      api.items.list(),
    ])
    const [cats, cellResult, debtList, rsList, recList, bankList, deptList, settingList, itemList] = results

    // 失敗したAPIはコンソールに警告（権限不足は正常動作）
    const labels = ['categories', 'cells', 'debts', 'reschedules', 'recurring', 'banks', 'departments', 'settings', 'items']
    results.forEach((r, i) => {
      if (r.status === 'rejected') {
        console.warn(`[loadAll] ${labels[i]}: ${r.reason?.message || r.reason}`)
      }
    })

    set({
      categories: cats.status === 'fulfilled' ? cats.value : { income: [], expense: [] },
      cells: cellResult.status === 'fulfilled' ? cellResult.value.cells : {},
      cellMeta: cellResult.status === 'fulfilled' ? cellResult.value.cellMeta : {},
      debts: debtList.status === 'fulfilled' ? debtList.value : [],
      reschedules: rsList.status === 'fulfilled' ? rsList.value : [],
      recurring: recList.status === 'fulfilled' ? recList.value : [],
      banks: bankList.status === 'fulfilled' ? bankList.value : { accounts: [], balances: {} },
      departments: deptList.status === 'fulfilled' ? deptList.value : [],
      items: itemList.status === 'fulfilled' ? itemList.value : [],
      settings: settingList.status === 'fulfilled' ? settingList.value : { settings: {}, fiscalYears: [], openingBalances: {} },
      loading: false,
    })
  },

  saveCell: async (vendorId, date, amount) => {
    const key = `${vendorId}_${date}`
    const toast = useToastStore.getState()
    const ui = useUIStore.getState()
    const history = useHistoryStore.getState()
    const state = get()
    const vendor = [...state.categories.income, ...state.categories.expense]
      .flatMap((c) => c.vendors).find((v) => v.id === vendorId)
    const prevAmount = state.cells[key] || 0
    // 楽観的更新
    set((s) => ({ cells: { ...s.cells, [key]: amount } }))
    ui.incrementPending()
    try {
      await api.cells.save({ cells: { [key]: amount } })
      // 監査ログは 2026-05-24 CRITICAL C-2 対応で functions/api/cells.ts のサーバ側に集約済み。
      // フロントから api.audit.log() を呼ぶと二重記録 + DevTools 直叩きでログ偽装可能だったため撤去。
      // 履歴に積む（変化があった場合のみ）
      if (prevAmount !== amount) {
        const vname2 = vendor?.name || vendorId
        const label = prevAmount === 0
          ? `${vname2} / ${date} に ${amount.toLocaleString()}円 を登録`
          : amount === 0
            ? `${vname2} / ${date} の ${prevAmount.toLocaleString()}円 を削除`
            : `${vname2} / ${date}: ${prevAmount.toLocaleString()} → ${amount.toLocaleString()}円`
        history.push({
          id: newHistId(),
          label,
          timestamp: Date.now(),
          undo: async () => { await get().saveCell(vendorId, date, prevAmount) },
          redo: async () => { await get().saveCell(vendorId, date, amount) },
        })
      }
    } catch (err) {
      // ロールバック
      set((s) => {
        const next = { ...s.cells }
        if (prevAmount === 0) delete next[key]
        else next[key] = prevAmount
        return { cells: next }
      })
      toast.error(`保存失敗: ${err instanceof Error ? err.message : '不明なエラー'}`)
      throw err
    } finally {
      ui.decrementPending()
    }
  },

  saveCellMeta: async (vendorId, date, meta) => {
    const key = `${vendorId}_${date}`
    const toast = useToastStore.getState()
    const ui = useUIStore.getState()
    const history = useHistoryStore.getState()
    const state = get()
    const vendor = [...state.categories.income, ...state.categories.expense]
      .flatMap((c) => c.vendors).find((v) => v.id === vendorId)
    const prev = state.cellMeta[key]
    set((s) => ({
      cellMeta: { ...s.cellMeta, [key]: { ...s.cellMeta[key], ...meta } as import('../types').CellMeta },
    }))
    ui.incrementPending()
    try {
      await api.cells.save({ cellMeta: { [key]: meta } })
      // 監査ログは 2026-05-24 CRITICAL C-2 対応で functions/api/cells.ts のサーバ側に集約済み。
      // フロントから api.audit.log() を呼ぶと二重記録 + DevTools 直叩きでログ偽装可能だったため撤去。
      // 履歴に積む（何か変化があれば）
      const changed = (meta.status && meta.status !== prev?.status)
        || (meta.note !== undefined && meta.note !== (prev?.note || ''))
      if (changed) {
        const labels: Record<string, string> = { pending: '予定', completed: '完了', rescheduled: 'リスケ中' }
        const desc: string[] = []
        if (meta.status && meta.status !== prev?.status) {
          desc.push(`状態: ${labels[prev?.status || 'pending']}→${labels[meta.status]}`)
        }
        if (meta.note !== undefined && meta.note !== (prev?.note || '')) {
          desc.push('メモ変更')
        }
        const prevSnapshot: Partial<import('../types').CellMeta> = {
          status: prev?.status,
          note: prev?.note,
          actualDate: prev?.actualDate,
        }
        const vname = vendor?.name || vendorId
        history.push({
          id: newHistId(),
          label: `${vname} / ${date}: ${desc.join(', ')}`,
          timestamp: Date.now(),
          undo: async () => { await get().saveCellMeta(vendorId, date, prevSnapshot) },
          redo: async () => { await get().saveCellMeta(vendorId, date, meta) },
        })
      }
    } catch (err) {
      // ロールバック
      set((s) => {
        const next = { ...s.cellMeta }
        if (prev === undefined) delete next[key]
        else next[key] = prev
        return { cellMeta: next }
      })
      toast.error(`保存失敗: ${err instanceof Error ? err.message : '不明なエラー'}`)
      throw err
    } finally {
      ui.decrementPending()
    }
  },

  // ===== 監査ログ撤去（CRITICAL C-2・2026-05-24） =====
  //   旧版は vendors / categories / banks / settings の各サーバー endpoint が writeAudit を
  //   呼んだ上で、フロントからも api.audit.log を二重呼び出ししていた。
  //   - 二重ログで /history が冗長
  //   - DevTools 直叩きでログ偽装可能（C-3 で別途対策）
  //   サーバー側 writeAudit に一本化し、フロント側は何もしない。
  addVendor: async (data) => {
    await api.vendors.create(data)
    const cats = await api.categories.list()
    set({ categories: cats })
  },

  updateVendor: async (data) => {
    await api.vendors.update(data)
    const cats = await api.categories.list()
    set({ categories: cats })
  },

  removeVendor: async (id) => {
    await api.vendors.remove(id)
    const cats = await api.categories.list()
    set({ categories: cats })
  },

  addCategory: async (data) => {
    await api.categories.create(data)
    const cats = await api.categories.list()
    set({ categories: cats })
  },

  updateCategory: async (data) => {
    await api.categories.update(data)
    const cats = await api.categories.list()
    set({ categories: cats })
  },

  removeCategory: async (id) => {
    await api.categories.remove(id)
    const cats = await api.categories.list()
    set({ categories: cats })
  },

  setOpeningBalance: async (date: string, amount: number) => {
    // 楽観更新
    set((s) => ({
      settings: {
        ...s.settings,
        openingBalances: { ...s.settings.openingBalances, [date]: amount },
      },
    }))
    await api.settings.setOpeningBalance(date, amount)
    // 監査ログは functions/api/settings.ts のサーバ側 writeAudit に集約済み（C-2 対応）
  },

  setBankBalance: async (accountId: string, date: string, balance: number) => {
    const key = `${accountId}_${date}`
    // 楽観更新
    set((s) => ({
      banks: {
        ...s.banks,
        balances: { ...s.banks.balances, [key]: balance },
      },
    }))
    await api.banks.updateBalance({ accountId, date, balance })
    // 監査ログは functions/api/banks.ts のサーバ側 writeAudit に集約済み（C-2 対応）
  },

  loadReschedules: async () => {
    const rs = await api.reschedules.list()
    set({ reschedules: rs })
  },

  loadDebts: async () => {
    const d = await api.debts.list()
    set({ debts: d })
  },

  loadRecurring: async () => {
    const r = await api.recurring.list()
    set({ recurring: r })
  },

  loadBanks: async (from, to) => {
    const b = await api.banks.list(from, to)
    set({ banks: b })
  },

  loadSettings: async () => {
    const s = await api.settings.list()
    set({ settings: s })
  },
}))
